import { Component, OnInit, Input, EventEmitter, Output, OnChanges, SimpleChanges, OnDestroy } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
// services
import { MasterService } from '../../../../../services/master.service'
import { MessageService } from '../../../../../services/message.service'
import { SecurityService } from '../../../../../services/security.service'
import * as languageLibrary from '../../../../../services/language'
import { SCROLL_BOTTOM_OUT } from "src/app/constants/list";
import { DBL_CLICK_TIMEOUT } from "src/app/constants/edit";
import { NOTE_ACTIVITIES, NOTE_ACTIVITY_FILTERS } from "src/app/constants/note";
import { Note } from 'src/app/models/note.model';
import { NoteActivity } from 'src/app/models/note-activity.model';
import { Subscription } from "rxjs";

@Component({
    selector: 'notes',
    templateUrl: './notes.component.html',
    styleUrls: ['./notes.component.scss'],
    standalone: false
})
export class NotesComponent implements OnInit, OnChanges, OnDestroy {

    /*
    * variables
    */

    // The system module currently using this notes component. Used as a filter for the notes list.
    @Input() module: string | null = null

    // Unique internal identity of the associated contact
    @Input() contactId: number = 0

    // Unique internal identity of the associated inventory item
    @Input() inventoryId: number = 0

    // Unique internal identity of the associated sale
    @Input() saleId: number = 0

    // Unique internal identity of the associated service ticket
    @Input() serviceId: number = 0

    /** In order to have multiple components on a single page (tabs, etc...) we need a key to uniquely identify each one. */
    @Input() key: string | null = null

    // permissions
    @Input() permissions: string[] = []

    // Indicates the user is allowed to edit the notes.
    @Input() isEdit: boolean = true

    // 
    @Input() isFlat: boolean = false

    @Output() notes$: EventEmitter<Object> = new EventEmitter()

    @Output() posted: EventEmitter<Object> = new EventEmitter()

    /** A list of availble note activities to chose from. */
    protected availableActivities: NoteActivity[] = NOTE_ACTIVITIES

    /** A list of combined note activiies for use in list filters. */
    protected activityFilters: NoteActivity[] = NOTE_ACTIVITY_FILTERS

    /** A list of note acitivities to filter the list by. */
    protected activities: string | null = null

    protected notes: Note[] = []

    protected viewAlert: Boolean = false

    // save the language
    protected language: string = localStorage.getItem('language') ? localStorage.getItem('language') : 'EN'

    // set all words
    protected words = languageLibrary.language

    // define note selected
    protected noteSelected: number = 0

    // define the index of notes
    protected indexNote: number = 0

    // define if form window is open
    protected windowOpen: boolean = false

    // loading state
    protected loading: boolean = false

    // Current page the user is viewing (last loaded).
    protected page = 1

    // The number contacts currently being displayed.
    protected displayed = 0

    // Indicates whether or not there are no more records to load.
    protected exhausted = false

    // Total number of records available
    protected records: number

    // A handle to navbar event messages so we can unsubscribe later.
    private navbarWatcher: Subscription | null = null

    // A debounce timer for click events. Used to wait for 
    // the user to click twice before performing an action.
    private dblClickWatcher: ReturnType<typeof setTimeout> = null

    private subscriptions: Subscription[] = []

    // Group of inputs
    protected filters = new FormGroup({
        activity: new FormControl<string>(''),
        all: new FormControl<boolean>(false)
    })

    // Group of inputs
    protected information = new FormGroup({
        module: new FormControl<string | null>(null, [Validators.required]),
        activity: new FormControl<string>(this.availableActivities[0].key, [Validators.required]),
        content: new FormControl<string | null>(null, [Validators.required]),
        alert: new FormControl<boolean>(false)
    })

    // Group of inputs
    protected form = new FormGroup({
        id: new FormControl<number>(0, [Validators.required]),
        module: new FormControl<string | null>(null, [Validators.required]),
        activity: new FormControl<string>('none', [Validators.required]),
        content: new FormControl<string | null>(null, [Validators.required]),
        alert: new FormControl<boolean>(false),
        createdAt: new FormControl<string | null>(null),
        createdBy: new FormControl<string | null>(null)
    })

    /** Let the UI know whether or not it can remove a sale filter to get all contact notes. */
    get multi() { 
        return (this.contactId && this.saleId)
    }

    /*
    * life cycles
    */
    constructor(private master: MasterService, private ms: MessageService, public security: SecurityService) { }

    ngOnInit() {
        this.reset()
        this.fetch()

        this.subscriptions.push(
            this.ms.channelComponents$.subscribe(res => {
                if (res.message == "changeLanguage") {
                    this.language = res.value
                }
            })
        )

        this.subscriptions.push(
            this.filters.get('activity').valueChanges.subscribe(activity => {
                this.reset()
                this.fetch()
            })
        )

        this.subscriptions.push(
            this.filters.get('all').valueChanges.subscribe(all => {
                this.reset()
                this.fetch()
            })
        )
    }

    ngOnDestroy() {
        for (const sub of this.subscriptions) {
            sub.unsubscribe()
        }
        if (this.dblClickWatcher) {
            clearTimeout(this.dblClickWatcher)
        }
        if (!this.navbarWatcher) {
            return
        }

        this.navbarWatcher.unsubscribe()

        const modal = document.getElementById('notes-modal') as HTMLElement
        if (!modal) {
            return
        }

        modal.classList.remove('show')

        const backdrop = document.querySelector('div.modal-backdrop') as HTMLElement
        if (!backdrop) {
            return
        }

        backdrop.remove()
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!changes) {
            return
        }

        const first = Object.values(changes).some(c => c.isFirstChange())
        if (first) {
            return
        }

        if (changes.contactId || changes.serviceId || changes.inventoryId || changes.saleId) {
            this.reset()
            this.fetch()
        }
    }

    // get notes
    protected fetch = (): void => {
        let moduleFilter = this.module
        if (!moduleFilter || (moduleFilter.trim().length <= 0)) {
            moduleFilter = this.determineModule()
        }

        // if "all" is true we want to show all contact notes so remove any sale filter
        let allFilter = this.filters.get('all').value

        let moduleParam = ''
        if (moduleFilter && !allFilter) {
            moduleParam = `&module=${moduleFilter}`
        }

        let activityFilter = this.filters.get('activity').value
        let activityParam = ''
        if (activityFilter && (activityFilter.trim().length > 0)) {
            activityParam = `&activity=${activityFilter}`
        }

        let contactParam = ''
        if (this.contactId && (this.contactId > 0)) {
            contactParam = `&contact=${this.contactId}`
        }

        let inventoryParam = ''
        if (this.inventoryId && (this.inventoryId > 0)) {
            inventoryParam = `&inventory=${this.inventoryId}`
        }        

        let saleParam = ''
        if (this.saleId && (this.saleId > 0) && (!allFilter)) {
            saleParam = `&sale=${this.saleId}`
            contactParam = ''
        }

        let serviceParam = ''
        if (this.serviceId && (this.serviceId > 0)) {
            serviceParam = `&service=${this.serviceId}`
        }

        if (this.loading) {
            return
        }

        this.loading = true

        this.master.get(`notes?page=${this.page}&sort=posted|DESC${moduleParam}${activityParam}${contactParam}${inventoryParam}${saleParam}${serviceParam}`, res => {
            this.loading = false
            if (!res) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] })
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error })
                return
            }

            this.records = res.data.records
            this.displayed = Math.min(this.records, (res.data.page * res.data.ipp))
            this.exhausted = (res.data.next === null)

            if (this.page > 1) {
                this.notes = this.notes.concat(res.data.notes)
                this.alert()
                return
            }

            this.notes = res.data.notes
            this.alert()

            if (this.notes && (this.notes.length > 0)) {
                return
            }

            const addBox = (document.getElementById('addNoteCollapse') as HTMLElement)
            if (!addBox) {
                return
            }

            addBox.classList.add('show')
        })
    }

    /** Check the input identities to determine module or */
    private determineModule = (): string => {
        if (this.saleId && (this.saleId > 0)) {
            return 'sale'
        }

        if (this.contactId && (this.contactId > 0)) {
            return 'contacts'
        }

        if (this.inventoryId && (this.inventoryId > 0)) {
            return 'inventory'
        }

        if (this.serviceId && (this.serviceId > 0)) {
            return 'service'
        }

        return null
    }

    /**
     * Handle scroll events of the list container and load 
     * the next page when avilable and the user scrolled 
     * to the bottom.
     * @param e The scroll event that trigggered this handler.
     */
    protected scrolled = (e): void => {
        if (!e || !e.target) {
            return
        }

        if (this.exhausted) {
            return
        }

        if (this.loading) {
            return
        }

        const location = Math.abs(e.target.scrollHeight - e.target.clientHeight - e.target.scrollTop)

        if (location >= SCROLL_BOTTOM_OUT) {
            return
        }

        this.page = this.page + 1
        this.fetch()
    }

    // add new note
    protected save = (e): void => {
        e.preventDefault()

        if (this.information.invalid || !this.information.value) {
            return
        }

        const payload = {
            contactId: null,
            inventoryId: null,
            saleId: null,
            serviceId: null,
            module: this.information.get('module').value,
            activity: this.information.get('activity').value,
            alert: this.information.get('alert').value,
            content: this.information.get('content').value
        }

        if (!payload.module || (payload.module.length <= 0)) {
            payload.module = this.determineModule()
        }

        if (this.contactId && (this.contactId > 0)) {
            payload.contactId = this.contactId
        }

        if (this.inventoryId && (this.inventoryId > 0)) {
            payload.inventoryId = this.inventoryId
        }

        if (this.saleId && (this.saleId > 0)) {
            payload.saleId = this.saleId
        }

        if (this.serviceId && (this.serviceId > 0)) {
            payload.serviceId = this.serviceId
        }

        this.loading = true
        this.master.post(`notes`, payload, res => {
            this.loading = false
            if (!res) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] })
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error })
                return
            }
            if (this.information.get('alert').value == true) {
                this.windowOpen = true
            }

            this.posted.emit(payload)
            this.reset()
            this.fetch()
        })
    }

    protected reset = (): void => {
        let module = 'contacts'
        if (this.module && (this.module.length > 0)) {
            const modules = this.module.split(',')
            if (modules && (modules.length > 0)) {
                module = modules[0]
            }
        }

        this.information.reset()
        this.information.get('module').setValue(module)
        this.information.get('activity').setValue(this.availableActivities[0].key)
        this.noteSelected = 0
        this.indexNote = -1
    }

    /**
    * Check to see if the any existing notes are alertable 
    * and display the notes automatically if so.
    */
    protected alert = (): void => {
        if (!this.notes || (!Array.isArray(this.notes)) || (this.notes.length <= 0) || this.windowOpen) {
            return
        }

        for (const note of this.notes) {
            if (!note['alert']) {
                continue
            }

            this.viewAlert = true
            this.windowOpen = true

            const btn = document.getElementById(`btn-note-alert-contact_${this.key || ''}`)
            if (btn) {
                btn.click()
            }

            break
        }
    }

    /*
    todo: update functions
    */
    // set note form for update
    protected setNote = (note: Object, index: number): void => {
        if (!this.security.permitted('contacts', 'notes alerts', 'update')) {
            return
        }

        if (!this.isEdit) {
            return
        }

        if (this.dblClickWatcher !== null) {
            this.noteSelected = parseInt(note['id'])
            this.indexNote = index

            this.form.setValue({
                id: parseInt(note['id'] || 0),
                module: note['module'],
                activity: note['activity'],
                content: note['content'],
                alert: (note['alert'] == 1),
                createdAt: note['posted'],
                createdBy: note['creator']
            })
        }

        const ctx = this
        this.dblClickWatcher = setTimeout(() => {
            ctx.dblClickWatcher = null
        }, DBL_CLICK_TIMEOUT)
    }

    // send form for update
    protected update = (e): void => {
        e.preventDefault()

        if (this.form.invalid) {
            return
        }

        if (!this.noteSelected || (this.noteSelected <= 0)) {
            return
        }

        const payload = { 
            activity: this.form.get('activity').value,
            alert: this.form.get('alert').value,
            content: this.form.get('content').value
        }

        this.loading = true
        this.master.put(`notes/${this.noteSelected}`, payload, res => {
            this.loading = false
            if (!res) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] })
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error })
                return
            }

            this.reset()
            this.fetch()
        })
    }

    /*
    !: delete functions
    */
    protected discard = (e: Event, id, index: number): void => {
        e.preventDefault()

        if (!this.security.permitted('contacts', 'notes alerts', 'delete')) {
            return
        }

        if (!id || (index < 0) || (index > this.notes.length - 1)) {
            return
        }

        this.loading = true
        this.master.discard(`notes/${id}`, res => {
            this.loading = false

            if (!res) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] });
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error });
                return
            }

            this.reset()
            this.fetch()
        })
    }

    /**
     * Return the MDI or drip icon classes based on the activity key given.
     * @param key 
     */
    protected icon = (activity: string) => {
        if (!activity || (activity.length <= 0)) {
            return 'description'
        }

        switch (activity.toLowerCase()) {
            case 'email-in':
                return 'move_to_inbox'
            case 'email-out':
                return 'outbox'
            case 'sms-in':
                return 'sms'
            case 'sms-out':
                return 'comment'
            case 'call-in':
                return 'phone_callback'
            case 'call-out':
                return 'local_phone'
            case 'in-person':
                return 'person'
            default:
                return 'description'
        }
    }
}
